import { ClockIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Tooltip } from 'stories/components'
import type { Color } from 'stories/types'

import { SelectModal } from './SelectModal'

interface InputButtonProps {
  /**
   * What background color to use
   */
  color?: Color
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is readOnly
   */
  readOnly?: boolean
  /**
   * Tooltip of Input
   */
  tooltip?: string
  /**
   * Can choose multiple items
   */
  multiple?: boolean
  /**
   * Enable input text
   */
  enableInput?: boolean
  /**
   * Is autofocus
   */
  autoFocus?: boolean
  /**
   * Type of Input
   */
  type?: string
  /**
   * Title of Input
   */
  title?: string
  /**
   * Placeholder of Input
   */
  placeholder?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Value of Input
   */
  value?: string[] | string

  options?: Record<string, string>

  modalTitle?: string
  /**
   * Font Size of Input
   */
  fontSize?: number
  /**
   * Error of Input
   */
  error?: string
  /**
   * Custom class name
   */
  className?: string
  /**
   * Is has icon
   */
  hasIcon?: boolean
  /**
   * Is has icon
   */
  required?: boolean
  /**
   * Icon component
   */
  icon?: string | JSX.Element | null
  /**
   * Prefix
   */
  prefix?: string
  /**
   * onChange
   */
  history?: boolean
  searchable?: boolean
  additionalElements?: JSX.Element | null
  onChange: (e: any) => void // string | React.ChangeEvent<HTMLInputElement>) => void
  showHistory?: () => void
  onKeyPress?: (e: any) => void
}

/**
 * Primary UI component for user interaction
 */
export const InputWithButton = ({
  disabled = false,
  readOnly = false,
  multiple = true,
  enableInput = false,
  tooltip = '',
  autoFocus = false,
  type = 'text',
  title = '',
  placeholder = ' ',
  name = '',
  value = [],
  options = {},
  modalTitle = '',
  fontSize = 15,
  error = '',
  className = '',
  hasIcon = false,
  icon = null,
  prefix = '',
  history = false,
  required = false,
  searchable = true,
  additionalElements = null,
  onChange = () => {},
  showHistory = () => {},
}: InputButtonProps) => {
  const [isShowModal, setShowModal] = useState(false)

  const onSelect = () => {
    setShowModal(true)
  }

  const onSelectDone = (values: string[]) => {
    onChange(values)
    setShowModal(false)
  }

  const classNames = [
    'block',
    'rounded-xl',
    'px-2.5',
    'pb-[2px]',
    'pt-[27px]',
    'w-full',
    `text-[${fontSize}px]`,
    'text-gray-900',
    disabled ? 'bg-slate-100' : 'bg-white',
    readOnly ? 'cursor-not-allowed' : '',
    'border',
    'border-slate-300',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-shade-blue/20',
    'focus:bg-white',
    'transition',
    'hover:border-slate-400',
    'focus:border-shade-blue',
    'peer',
    prefix.length > 0 && 'pl-7',
    hasIcon && 'pl-9',
    error && 'border-rose-700',
  ]
  if (value === null) value = []

  const changeInput = (e: any) => {
    let _value = e.target.value
    if (type.toLowerCase() === 'email') _value = _value.trim().toLowerCase()
    onChange(_value)
  }

  return (
    <div className="input-container">
      <div className={`group relative z-0 w-full ${className}`}>
        {hasIcon && icon && typeof icon === 'string' && (
          <div className="flex absolute bottom-2 left-0 items-center pl-3 pointer-events-none">
            <img src={icon} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>
        )}
        {hasIcon && icon && typeof icon !== 'string' && (
          <div className="flex absolute bottom-2 left-0 items-center pl-3 pointer-events-none">{icon}</div>
        )}

        {prefix.length > 0 && (
          <div className="text-gray-600 text-[14px] flex absolute top-[29px] left-0 items-center pl-3 pointer-events-none">
            {prefix}
          </div>
        )}
        <input
          type={type}
          name={name}
          className={classNames.join(' ')}
          placeholder={placeholder}
          disabled={disabled || readOnly}
          autoFocus={autoFocus}
          value={Array.isArray(value) ? value.map((v) => options[v]).join(', ') : value}
          required={required}
          onChange={changeInput}
          readOnly={!enableInput}
        />
        <label className="absolute text-[12px] text-gray-700 top-1.5 z-10 origin-[0] left-2.5 flex gap-2 items-center">
          {title}
          {required && '*'}

          <button className="text-shade-blue" onClick={onSelect}>
            Select({Array.isArray(value) ? value.length : Object.values(options).includes(value) ? 1 : 0})
          </button>

          {tooltip.length > 0 ? <Tooltip message={tooltip}></Tooltip> : null}
          {history && (
            <span className="ml-1 hidden group-hover:inline" onClick={() => showHistory()}>
              <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
            </span>
          )}
          {additionalElements}
        </label>
      </div>
      {error && <p className="peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1">{error}</p>}

      {isShowModal && (
        <SelectModal
          searchable={searchable}
          options={options}
          title={modalTitle}
          multiple={multiple}
          value={value}
          onSubmit={onSelectDone}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  )
}
